import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FolderKanban, Search, Plus, MoreHorizontal } from 'lucide-react';
import { ORG_BASE } from '@/pages/services/organizations/organizations-service';
import { Container, PageHeader, Toolbar } from '@vnetwork/ui/layout';
import {
  DataTable,
  StatusPill,
  EmptyState,
  type ColumnDef,
} from '@vnetwork/ui/data';
import {
  Button,
  Input,
  Badge,
  Skeleton,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  IconButton,
} from '@vnetwork/ui/primitives';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useMyProjects,
  useArchiveProject,
  useDeleteProject,
  type Project,
} from '@/api/projects';
import { CreateProjectDialog } from './components/CreateProjectDialog';
import {
  PROJECT_STATUS_TONE,
  PROJECT_STATUS_LABEL,
  ROLE_TONE,
  ROLE_LABEL,
  formatDate,
} from './shared';

type PendingAction = { type: 'archive' | 'delete'; project: Project } | null;

export function ProjectList() {
  const navigate = useNavigate();
  const { show } = useToast();
  const { data: projects = [], isLoading } = useMyProjects();

  const [query, setQuery] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [pending, setPending] = useState<PendingAction>(null);

  const archive = useArchiveProject({
    onSuccess: () => {
      show({ tone: 'success', title: 'Project archived' });
      setPending(null);
    },
    onError: () => show({ tone: 'danger', title: 'Could not archive project' }),
  });
  const remove = useDeleteProject({
    onSuccess: () => {
      show({ tone: 'success', title: 'Project deleted' });
      setPending(null);
    },
    onError: () => show({ tone: 'danger', title: 'Could not delete project' }),
  });

  const data = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q
      ? projects.filter((p) => p.name.toLowerCase().includes(q))
      : projects;
  }, [projects, query]);

  const columns = useMemo<ColumnDef<Project, unknown>[]>(
    () => [
      {
        id: 'name',
        accessorKey: 'name',
        header: 'Project',
        size: 520,
        cell: ({ row }) => (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)', fontWeight: 500 }}>
            <FolderKanban size={14} color="var(--vn-fg-muted)" />
            {row.original.name}
          </span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        enableSorting: false,
        size: 100,
        cell: ({ row }) => (
          <StatusPill tone={PROJECT_STATUS_TONE[row.original.status]}>
            {PROJECT_STATUS_LABEL[row.original.status]}
          </StatusPill>
        ),
      },
      {
        id: 'role',
        header: 'Your role',
        enableSorting: false,
        size: 110,
        cell: ({ row }) => {
          const role = row.original.user_role;
          if (!role) return <span style={{ color: 'var(--vn-fg-muted)' }}>—</span>;
          return (
            <Badge tone={ROLE_TONE[role]} appearance="subtle" size="sm">
              {ROLE_LABEL[role]}
            </Badge>
          );
        },
      },
      {
        id: 'teams',
        header: 'Teams',
        size: 80,
        accessorFn: (p) => p.teams_count ?? 0,
        cell: ({ getValue }) => (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{getValue() as number}</span>
        ),
      },
      {
        id: 'created',
        header: 'Created',
        accessorKey: 'created_at',
        size: 96,
        cell: ({ row }) => (
          <span style={{ color: 'var(--vn-fg-muted)' }}>{formatDate(row.original.created_at)}</span>
        ),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => {
          const p = row.original;
          const isOwner = p.user_role === 'owner';
          return (
            <div style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <IconButton variant="ghost" size="sm" aria-label="Project actions">
                    <MoreHorizontal size={16} />
                  </IconButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={() => navigate(`${ORG_BASE}/projects/${p.id}`)}>
                    View details
                  </DropdownMenuItem>
                  {isOwner && p.status !== 'archived' && (
                    <DropdownMenuItem onSelect={() => setPending({ type: 'archive', project: p })}>
                      Archive
                    </DropdownMenuItem>
                  )}
                  {isOwner && (
                    <DropdownMenuItem
                      tone="danger"
                      onSelect={() => setPending({ type: 'delete', project: p })}
                    >
                      Delete
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
      },
    ],
    [navigate],
  );

  return (
    <Container size="xl">
      <PageHeader
        icon={<FolderKanban size={22} />}
        title="Projects"
        description="Group teams and resources, and control who can access them."
        actions={
          <Button startIcon={<Plus size={15} />} onClick={() => setCreateOpen(true)}>
            Create project
          </Button>
        }
      />

      <Toolbar
        start={
          <Input
            placeholder="Search projects"
            startSlot={<Search />}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ maxWidth: 320 }}
          />
        }
      />

      {isLoading ? (
        <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} style={{ height: 48 }} />
          ))}
        </div>
      ) : projects.length === 0 ? (
        <EmptyState
          icon={<FolderKanban size={28} />}
          title="No projects yet"
          description="Create your first project to start grouping teams and resources."
          actions={
            <Button startIcon={<Plus size={15} />} onClick={() => setCreateOpen(true)}>
              Create project
            </Button>
          }
        />
      ) : (
        <DataTable
          columns={columns}
          data={data}
          getRowId={(r) => r.id}
          onRowClick={(r) => navigate(`${ORG_BASE}/projects/${r.id}`)}
          emptyState="No projects match your search."
        />
      )}

      <CreateProjectDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(id) => navigate(`${ORG_BASE}/projects/${id}`)}
      />

      <ConfirmDialog
        open={pending?.type === 'archive'}
        onOpenChange={(o) => !o && setPending(null)}
        title="Archive project?"
        description={`“${pending?.project.name}” will be hidden but can be restored later.`}
        confirmLabel="Archive"
        loading={archive.isPending}
        onConfirm={() => pending && archive.mutate(pending.project.id)}
      />

      <ConfirmDialog
        open={pending?.type === 'delete'}
        onOpenChange={(o) => !o && setPending(null)}
        title="Delete project?"
        description={`This permanently deletes “${pending?.project.name}”. This action cannot be undone.`}
        confirmLabel="Delete"
        destructive
        requireText={pending?.type === 'delete' ? pending.project.name : undefined}
        loading={remove.isPending}
        onConfirm={() => pending && remove.mutate(pending.project.id)}
      />
    </Container>
  );
}
